const APPSCRIPT_URL =
  import.meta.env.VITE_APPSCRIPT_URL || 'YOUR_APPSCRIPT_DEPLOYMENT_URL_HERE';

class AppScriptAPI {
  setUserEmail(email) {
    this._userEmail = email;
  }

  /**
   * Use JSONP for read operations (no CORS issues)
   */
  async callGet(action, data = {}) {
    return new Promise((resolve, reject) => {
      console.log('📤 JSONP GET Request:', action, data);

      // Create unique callback name
      const callbackName =
        'jsonpCallback_' +
        Date.now() +
        '_' +
        Math.random().toString(36).substr(2, 9);

      // Set up global callback
      window[callbackName] = (response) => {
        console.log('📥 JSONP GET Response:', response);

        // Cleanup
        delete window[callbackName];
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }

        if (response.success === false && response.error) {
          reject(new Error(response.error));
        } else {
          resolve(response);
        }
      };

      // Build URL with parameters
      const params = new URLSearchParams({
        callback: callbackName,
        action: action,
        data: JSON.stringify(data),
      });

      const url = `${APPSCRIPT_URL}?${params.toString()}`;
      console.log('🌐 JSONP GET URL:', url.substring(0, 100) + '...');

      // Create script element
      const script = document.createElement('script');
      script.src = url;

      // Error handling
      script.onerror = () => {
        console.error('❌ Script loading failed');
        delete window[callbackName];
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
        reject(new Error('Failed to load data from server'));
      };

      // Add script to page
      document.body.appendChild(script);

      // Timeout after 30 seconds
      setTimeout(() => {
        if (window[callbackName]) {
          console.error('⏱️ Request timeout');
          delete window[callbackName];
          if (document.body.contains(script)) {
            document.body.removeChild(script);
          }
          reject(new Error('Request timeout - no response after 30 seconds'));
        }
      }, 30000);
    });
  }

  /**
   * Use JSONP for write operations (bypasses CORS completely)
   */
  async callPost(action, data = {}) {
    // Attach the current user's email to every write request for backend access control
    const payload = this._userEmail ? { ...data, _userEmail: this._userEmail } : data;
    return new Promise((resolve, reject) => {
      console.log('📤 JSONP POST Request:', action, payload);

      // Create unique callback name
      const callbackName =
        'jsonpCallback_' +
        Date.now() +
        '_' +
        Math.random().toString(36).substr(2, 9);

      // Set up global callback
      window[callbackName] = (response) => {
        console.log('📥 JSONP Response received:', response);

        // Cleanup
        delete window[callbackName];
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }

        if (response.success === false) {
          reject(
            new Error(response.error || response.message || 'Operation failed')
          );
        } else {
          resolve(response);
        }
      };

      // Build URL with parameters
      const params = new URLSearchParams({
        callback: callbackName,
        action: action,
        data: JSON.stringify(payload),
      });

      const url = `${APPSCRIPT_URL}?${params.toString()}`;
      console.log('🌐 JSONP URL:', url.substring(0, 100) + '...');

      // Create script element
      const script = document.createElement('script');
      script.src = url;

      // Error handling
      script.onerror = () => {
        console.error('❌ Script loading failed');
        delete window[callbackName];
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
        reject(new Error('Failed to load script from server'));
      };

      // Add script to page
      document.body.appendChild(script);

      // Timeout after 30 seconds
      setTimeout(() => {
        if (window[callbackName]) {
          console.error('⏱️ Request timeout');
          delete window[callbackName];
          if (document.body.contains(script)) {
            document.body.removeChild(script);
          }
          reject(new Error('Request timeout - no response after 30 seconds'));
        }
      }, 30000);
    });
  }

  handleError(error) {
    return new Error(error.message || 'An error occurred');
  }

  // ============================================
  // READ OPERATIONS (Use JSONP)
  // ============================================

  // Location & Search
  async getLocationData() {
    return this.callGet('getLocationData');
  }

  async searchRecord(searchData) {
    return this.callGet('searchRecord', searchData);
  }

  async getLocationDataForAnalytics() {
    return this.callGet('getLocationDataForAnalytics');
  }

  async getAnalytics(level, code, sheetId) {
    return this.callGet('getAnalytics', { level, code, sheetId });
  }

  async getGNDivisionsByPrefix(prefix) {
    return this.callGet('getGNDivisionsByPrefix', { prefix });
  }

  // Update Page Data - GET Methods
  async getBasicInfo(recordId, sheetId) {
    return this.callGet('getBasicInfo', {
      uniqueId: recordId,
      sheetId: sheetId,
    });
  }

  async getContactDetails(recordId, sheetId) {
    return this.callGet('getContactDetails', {
      uniqueId: recordId,
      sheetId: sheetId,
    });
  }

  async getPersonalDetails(recordId, sheetId) {
    return this.callGet('getPersonalDetails', {
      uniqueId: recordId,
      sheetId: sheetId,
    });
  }

  async getHousingDetails(recordId, sheetId) {
    return this.callGet('getHousingDetails', {
      uniqueId: recordId,
      sheetId: sheetId,
    });
  }

  async getDisabilityDetails(recordId, sheetId) {
    return this.callGet('getDisabilityDetails', {
      uniqueId: recordId,
      sheetId: sheetId,
    });
  }

  async getEducationDetails(recordId, sheetId) {
    return this.callGet('getEducationDetails', {
      uniqueId: recordId,
      sheetId: sheetId,
    });
  }

  async getVocationalTrainingDetails(recordId, sheetId) {
    return this.callGet('getVocationalTrainingDetails', {
      uniqueId: recordId,
      sheetId: sheetId,
    });
  }

  async getEconomicActivityDetails(recordId, sheetId) {
    return this.callGet('getEconomicActivityDetails', {
      uniqueId: recordId,
      sheetId: sheetId,
    });
  }

  async getAssistanceDetails(recordId, sheetId) {
    return this.callGet('getAssistanceDetails', {
      uniqueId: recordId,
      sheetId: sheetId,
    });
  }

  async getContributionAtHomeDetails(recordId, sheetId) {
    return this.callGet('getContributionAtHomeDetails', {
      uniqueId: recordId,
      sheetId: sheetId,
    });
  }

  async getWelfareAssistanceDetails(recordId, sheetId) {
    return this.callGet('getWelfareAssistanceDetails', {
      uniqueId: recordId,
      sheetId: sheetId,
    });
  }

  async getRehabilitationPlanDetails(recordId, sheetId) {
    return this.callGet('getRehabilitationPlanDetails', {
      uniqueId: recordId,
      sheetId: sheetId,
    });
  }

  async checkUserExists(email) {
    return this.callGet('checkUserExists', { email });
  }

  async getUserPermissions(email) {
    return this.callGet('getUserPermissions', { email });
  }

  async getAllUsers() {
    return this.callGet('getAllUsers');
  }

  // Dashboard & Admin
  async getDashboardData() {
    return this.callGet('getDashboardData', this._userEmail ? { _userEmail: this._userEmail } : {});
  }

  async getUsers(params = {}) {
    return this.callGet('getUsers', params);
  }

  async getData(params) {
    return this.callGet('getData', params);
  }

  async getReports(params = {}) {
    return this.callGet('getReports', params);
  }

  // ============================================
  // WRITE OPERATIONS (Use JSONP)
  // ============================================

  // Registration
  async submitRegistration(formData) {
    return this.callPost('submitRegistration', formData);
  }

  // Update Page Data - POST Methods
  async updateBasicInfo(updateData) {
    return this.callPost('updateBasicInfo', updateData);
  }

  async updateContactDetails(updateData) {
    return this.callPost('updateContactDetails', updateData);
  }

  async updatePersonalDetails(updateData) {
    return this.callPost('updatePersonalDetails', updateData);
  }

  async updateHousingDetails(updateData) {
    return this.callPost('updateHousingDetails', updateData);
  }

  async updateDisabilityDetails(updateData) {
    return this.callPost('updateDisabilityDetails', updateData);
  }

  async updateEducationDetails(updateData) {
    return this.callPost('updateEducationDetails', updateData);
  }

  async updateVocationalTrainingDetails(updateData) {
    return this.callPost('updateVocationalTrainingDetails', updateData);
  }

  async updateEconomicActivityDetails(updateData) {
    return this.callPost('updateEconomicActivityDetails', updateData);
  }

  async updateAssistanceDetails(updateData) {
    return this.callPost('updateAssistanceDetails', updateData);
  }

  async updateContributionAtHomeDetails(updateData) {
    return this.callPost('updateContributionAtHomeDetails', updateData);
  }

  async updateWelfareAssistanceDetails(updateData) {
    return this.callPost('updateWelfareAssistanceDetails', updateData);
  }

  async updateRehabilitationPlanDetails(updateData) {
    return this.callPost('updateRehabilitationPlanDetails', updateData);
  }

  async addNewUser(userData) {
    return this.callPost('addNewUser', userData);
  }

  async updateLastLogin(email) {
    return this.callPost('updateLastLogin', { email });
  }

  // Admin Operations
  async updateUserPermissions(userData) {
    return this.callPost('updateUserPermissions', userData);
  }

  async updateUserStatus(email, status) {
    return this.callPost('updateUserStatus', { email, status });
  }

  async deleteUser(email) {
    return this.callPost('deleteUser', { email });
  }

  async markAsDeceased(uniqueId, sheetId, dateOfDeath) {
    return this.callPost('markAsDeceased', { uniqueId, sheetId, dateOfDeath });
  }

  async createUser(userData) {
    return this.callPost('createUser', userData);
  }

  async updateUser(userId, userData) {
    return this.callPost('updateUser', { userId, ...userData });
  }

  async saveData(data) {
    return this.callPost('saveData', data);
  }

  async updateData(data) {
    return this.callPost('updateData', data);
  }

  async deleteData(params) {
    return this.callPost('deleteData', params);
  }
}

export default new AppScriptAPI();
